import { fetchCustomers, fetchCustomerBookings, } from '../../../api/adminApi';
import { i18nService } from '../../../i18n/I18nService';
import { buildMonthDays } from '../../calendarGrid';
import { t, createScreenHeader, createBackButton, createCustomerField, formatDurationFromMinutes, isUnauthorized, } from './context';
import { dateKeyLocal as dateKey, isSameLocalDay as isSameDay } from '../../../utils/format';
// `DD.MM.YYYY` for the "Selected date" label.
function formatDisplayDate(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}.${date.getFullYear()}`;
}
// Booking instants are Unix seconds on the UTC calendar-day convention the app
// uses everywhere — render them with UTC getters so the day/time matches how
// the slot was booked, with no per-viewer timezone shift.
function formatBookingDateTime(dateStart, dateEnd) {
    const start = new Date(dateStart * 1000);
    const end = new Date(dateEnd * 1000);
    const day = String(start.getUTCDate()).padStart(2, '0');
    const month = String(start.getUTCMonth() + 1).padStart(2, '0');
    const year = start.getUTCFullYear();
    const startTime = `${String(start.getUTCHours()).padStart(2, '0')}:${String(start.getUTCMinutes()).padStart(2, '0')}`;
    const endTime = `${String(end.getUTCHours()).padStart(2, '0')}:${String(end.getUTCMinutes()).padStart(2, '0')}`;
    return `${day}.${month}.${year}, ${startTime}–${endTime}`;
}
// "Today" as a UTC calendar day, materialised as a local-midnight Date whose
// local Y/M/D equals the UTC day. The rest of the picker reads local getters
// (dateKey, isSameDay, buildMonthDays), so anchoring here to the UTC day keeps
// the presets and the "today" marker aligned with the UTC calendar-day
// convention the server filters on and formatBookingDateTime renders in —
// otherwise, near local midnight an admin outside UTC would pick a day off by
// one from what the results show.
function startOfToday() {
    const now = new Date();
    return new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}
function daysAgo(days) {
    const date = startOfToday();
    date.setDate(date.getDate() - days);
    return date;
}
export function showBookingHistoryScreen(ctx) {
    ctx.screen.innerHTML = '';
    const header = createScreenHeader(t('admin.bookingHistory.title'));
    const content = document.createElement('div');
    content.className = 'admin__content admin__form';
    content.textContent = t('admin.bookingHistory.loading');
    const backButton = createBackButton(ctx, t('admin.bookingHistory.back'));
    ctx.screen.append(header, content, backButton);
    fetchCustomers()
        .then((customers) => {
        renderBookingHistoryContent(ctx, content, customers);
    })
        .catch((err) => {
        if (isUnauthorized(err)) {
            ctx.showLogin();
            return;
        }
        console.error('Failed to load customers:', err);
        content.textContent = t('admin.bookingHistory.loadError');
    });
}
function renderBookingHistoryContent(ctx, content, customers) {
    content.innerHTML = '';
    const range = { from: null, to: null };
    // --- Customer dropdown ---------------------------------------------------
    const { wrapper: customerWrapper, select: customerSelect } = createCustomerField(t('admin.bookingHistory.customerLabel'), t('admin.bookingHistory.customerPlaceholder'), customers);
    // --- Range preset buttons + custom-dates toggle --------------------------
    const rangeButtons = document.createElement('div');
    rangeButtons.className = 'admin__range-buttons';
    const lastWeekButton = createRangeButton(t('admin.bookingHistory.lastWeek'));
    const lastMonthButton = createRangeButton(t('admin.bookingHistory.lastMonth'));
    const customButton = createRangeButton(t('admin.bookingHistory.customDates'));
    rangeButtons.append(lastWeekButton, lastMonthButton, customButton);
    // --- Custom-dates range picker (hidden until toggled) --------------------
    const calendar = buildRangeCalendar(range, () => {
        // A manual pick means neither quick preset is the active range anymore.
        setActivePreset(null);
        onRangeChanged();
    });
    calendar.element.hidden = true;
    // --- Selected-range label ------------------------------------------------
    const rangeLabel = document.createElement('p');
    rangeLabel.className = 'admin__range-label';
    rangeLabel.hidden = true;
    // --- Show button + results ----------------------------------------------
    const showButton = document.createElement('button');
    showButton.type = 'button';
    showButton.className = 'cta admin__submit';
    showButton.textContent = t('admin.bookingHistory.showButton');
    showButton.disabled = true;
    const results = document.createElement('div');
    results.className = 'admin__booking-results';
    content.append(customerWrapper, rangeButtons, calendar.element, rangeLabel, showButton, results);
    function setActivePreset(active) {
        for (const button of [lastWeekButton, lastMonthButton]) {
            button.classList.toggle('admin__range-btn--active', button === active);
        }
    }
    function updateSubmitState() {
        showButton.disabled = !customerSelect.value || !range.from || !range.to;
    }
    function updateRangeLabel() {
        if (range.from && range.to) {
            rangeLabel.hidden = false;
            rangeLabel.textContent = t('admin.bookingHistory.selectedRange', {
                from: formatDisplayDate(range.from),
                to: formatDisplayDate(range.to),
            });
        }
        else {
            rangeLabel.hidden = true;
            rangeLabel.textContent = '';
        }
    }
    // Any change to the range or customer invalidates a table already shown.
    function onRangeChanged() {
        updateRangeLabel();
        updateSubmitState();
        results.innerHTML = '';
    }
    function applyPreset(fromDate, button) {
        range.from = fromDate;
        range.to = startOfToday();
        setActivePreset(button);
        calendar.refresh();
        onRangeChanged();
    }
    lastWeekButton.addEventListener('click', () => applyPreset(daysAgo(7), lastWeekButton));
    lastMonthButton.addEventListener('click', () => applyPreset(daysAgo(30), lastMonthButton));
    customButton.addEventListener('click', () => {
        calendar.element.hidden = !calendar.element.hidden;
        customButton.classList.toggle('admin__range-btn--active', !calendar.element.hidden);
    });
    customerSelect.addEventListener('change', () => {
        updateSubmitState();
        results.innerHTML = '';
    });
    showButton.addEventListener('click', () => {
        const userId = Number(customerSelect.value);
        if (!userId || !range.from || !range.to)
            return;
        results.innerHTML = '';
        results.textContent = t('admin.bookingHistory.loading');
        fetchCustomerBookings(userId, dateKey(range.from), dateKey(range.to), i18nService.getLocale())
            .then((bookings) => {
            renderBookingsTable(results, bookings);
        })
            .catch((err) => {
            if (isUnauthorized(err)) {
                ctx.showLogin();
                return;
            }
            console.error('Failed to load customer bookings:', err);
            results.innerHTML = '';
            results.textContent = t('admin.bookingHistory.bookingsLoadError');
        });
    });
}
function createRangeButton(label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'admin__range-btn';
    button.textContent = label;
    return button;
}
// A month calendar with two-click range selection: first click sets the start
// (clearing any end), second click sets the end (swapping if it lands before
// the start). Forked from the special-days calendar in workingHours.ts — same
// `admin__cal-*` markup and geometry (`buildMonthDays`), different selection
// model, and no past-date disabling since history lives in the past.
function buildRangeCalendar(range, onPick) {
    const today = startOfToday();
    let viewYear = today.getFullYear();
    let viewMonth = today.getMonth();
    const calendar = document.createElement('div');
    calendar.className = 'admin__cal';
    const calHead = document.createElement('div');
    calHead.className = 'admin__cal-head';
    const prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = 'admin__cal-nav';
    prevButton.setAttribute('aria-label', t('admin.bookingHistory.prevMonthLabel'));
    prevButton.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'admin__cal-nav';
    nextButton.setAttribute('aria-label', t('admin.bookingHistory.nextMonthLabel'));
    nextButton.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const monthLabel = document.createElement('span');
    monthLabel.className = 'admin__cal-month';
    calHead.append(prevButton, monthLabel, nextButton);
    const weekdaysRow = document.createElement('div');
    weekdaysRow.className = 'admin__cal-weekdays';
    for (let day = 0; day < 7; day += 1) {
        const cell = document.createElement('span');
        cell.className = 'admin__cal-weekday';
        cell.textContent = t(`booking.calendar.weekdays.${day}`);
        weekdaysRow.appendChild(cell);
    }
    const calGrid = document.createElement('div');
    calGrid.className = 'admin__cal-grid';
    calendar.append(calHead, weekdaysRow, calGrid);
    function selectDate(date) {
        if (!range.from || range.to) {
            // Start a fresh range.
            range.from = date;
            range.to = null;
        }
        else if (date < range.from) {
            // Second click lands before the start — swap so from <= to.
            range.to = range.from;
            range.from = date;
        }
        else {
            range.to = date;
        }
        renderCalendarGrid();
        onPick();
    }
    function renderCalendarGrid() {
        monthLabel.textContent = `${t(`booking.calendar.months.${viewMonth}`)} ${viewYear}`;
        calGrid.innerHTML = '';
        for (const date of buildMonthDays(viewYear, viewMonth)) {
            if (!date) {
                const empty = document.createElement('span');
                empty.className = 'admin__cal-day admin__cal-day--empty';
                calGrid.appendChild(empty);
                continue;
            }
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'admin__cal-day';
            button.textContent = String(date.getDate());
            if (isSameDay(date, today))
                button.classList.add('admin__cal-day--today');
            if (range.from && isSameDay(date, range.from))
                button.classList.add('admin__cal-day--range-start');
            if (range.to && isSameDay(date, range.to))
                button.classList.add('admin__cal-day--range-end');
            if (range.from && range.to && date > range.from && date < range.to) {
                button.classList.add('admin__cal-day--in-range');
            }
            button.addEventListener('click', () => selectDate(date));
            calGrid.appendChild(button);
        }
    }
    prevButton.addEventListener('click', () => {
        viewMonth -= 1;
        if (viewMonth < 0) {
            viewMonth = 11;
            viewYear -= 1;
        }
        renderCalendarGrid();
    });
    nextButton.addEventListener('click', () => {
        viewMonth += 1;
        if (viewMonth > 11) {
            viewMonth = 0;
            viewYear += 1;
        }
        renderCalendarGrid();
    });
    renderCalendarGrid();
    return { element: calendar, refresh: renderCalendarGrid };
}
function renderBookingsTable(container, bookings) {
    container.innerHTML = '';
    if (bookings.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'admin__table-empty';
        empty.textContent = t('admin.bookingHistory.empty');
        container.appendChild(empty);
        return;
    }
    const table = document.createElement('table');
    table.className = 'admin__table';
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    const headers = [
        t('admin.bookingHistory.table.plan'),
        t('admin.bookingHistory.table.place'),
        t('admin.bookingHistory.table.duration'),
        t('admin.bookingHistory.table.datetime'),
    ];
    for (const label of headers) {
        const th = document.createElement('th');
        th.textContent = label;
        headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    table.appendChild(thead);
    const tbody = document.createElement('tbody');
    for (const booking of bookings) {
        const row = document.createElement('tr');
        const planCell = document.createElement('td');
        planCell.textContent =
            booking.planName ?? (booking.paymentType === 'hourly' ? t('admin.bookingHistory.payPerHour') : '—');
        const placeCell = document.createElement('td');
        placeCell.textContent = booking.placeName;
        const durationCell = document.createElement('td');
        durationCell.textContent = formatDurationFromMinutes(booking.durationMinutes);
        const dateTimeCell = document.createElement('td');
        dateTimeCell.textContent = formatBookingDateTime(booking.dateStart, booking.dateEnd);
        row.append(planCell, placeCell, durationCell, dateTimeCell);
        tbody.appendChild(row);
    }
    table.appendChild(tbody);
    container.appendChild(table);
}
